// Bulk project import (internal only) — upload CSV/XLSX → map columns →
// validate (dry-run) → commit. Creates/updates sites, buildings, projects & contacts.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, ArrowLeft, Download, Wand2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

const FIELD_LABELS = {
    client_name: "Client *", site_name: "Site", site_address: "Site address",
    building_name: "Building", building_type: "Building type", units: "Units", storeys: "Storeys",
    project_name: "Project name", project_reference: "Project reference",
    project_type: "Project type", riba_stage: "RIBA stage", cdm_role: "CDM role",
    f10_number: "F10 number", target_date: "Target date", value_gbp: "Value (£)",
    contact_role: "Contact role", contact_name: "Contact name", contact_email: "Contact email",
    contact_phone: "Contact phone", contact_organisation: "Contact organisation",
    access_required: "Access required", info_only: "Info only", contact_notes: "Contact notes",
};

export default function ProjectImportPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const fileRef = useRef(null);
    const [fields, setFields] = useState([]);
    const [parsed, setParsed] = useState(null);     // {columns, rows, row_count, suggested_mapping}
    const [mapping, setMapping] = useState({});
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);     // dry-run or commit result
    const [committed, setCommitted] = useState(false);
    const [fileName, setFileName] = useState("");

    const isInternal = user && INTERNAL_ROLES.has(user.role);

    useEffect(() => { if (isInternal) api.get("/imports/projects/fields").then((r) => setFields(r.data.fields)).catch(() => {}); }, [isInternal]);

    if (user && !isInternal) {
        return (
            <div className="max-w-[640px] mx-auto py-16 text-center" data-testid="project-import-denied">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: "#C68A1C" }} />
                <h1 className="text-xl font-bold text-[#1F1F1F]">Internal access only</h1>
                <p className="text-[13px] mt-1.5" style={{ color: "#3e4b63" }}>Bulk project import is available to Priority Risk staff only.</p>
                <button onClick={() => navigate("/app/projects")} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[9px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                    <ArrowLeft className="w-4 h-4" /> Back to projects
                </button>
            </div>
        );
    }

    async function onFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name); setResult(null); setCommitted(false);
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post("/imports/projects/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setParsed(data);
            setMapping(data.suggested_mapping || {});
            toast.success(`Parsed ${data.row_count} row(s)`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not parse file");
            setParsed(null);
        } finally { setBusy(false); }
    }

    async function run(dryRun) {
        if (!mapping.client_name) { toast.error("Map the Client column first"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/imports/projects/commit", { mapping, rows: parsed.rows, dry_run: dryRun });
            setResult(data);
            if (!dryRun) {
                setCommitted(true);
                toast.success("Import complete");
            } else if (data.ok) {
                toast.success("Validation passed — ready to import");
            } else {
                toast.error(`${data.errors.length} row error(s) — fix and re-validate`);
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
        } finally { setBusy(false); }
    }

    async function downloadTemplate() {
        try {
            const { data } = await api.get("/imports/projects/template", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a"); a.href = url; a.download = "project_import_template.csv"; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not download template"); }
    }

    const c = result?.counters;

    return (
        <div className="max-w-[1000px] mx-auto space-y-5" data-testid="project-import-page">
            <button onClick={() => navigate("/app/projects")} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: "#3e4b63" }}>
                <ArrowLeft className="w-4 h-4" /> Back to projects
            </button>

            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">Import projects</h1>
                    <p className="text-[13px] mt-1" style={{ color: "#3e4b63" }}>Upload a CSV or XLSX to create/update sites, buildings, projects and project contacts. Existing records are updated, never duplicated.</p>
                </div>
                <button data-testid="import-download-template" onClick={downloadTemplate}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[9px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                    <Download className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Download template
                </button>
            </div>

            {/* Upload */}
            <div className="bg-white border rounded-[13px] shadow-brand-sm p-6" style={{ borderColor: "#E5E0DA" }}>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={onFile} className="hidden" data-testid="import-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={busy}
                    className="w-full border-2 border-dashed rounded-[12px] py-10 grid place-items-center gap-2 hover:bg-[#FBF8F3] transition-colors" style={{ borderColor: "#E0D8CC" }}>
                    {busy && !parsed ? <Loader2 className="w-7 h-7 animate-spin" style={{ color: "var(--brand-accent,#C75A1B)" }} /> : <UploadCloud className="w-7 h-7" style={{ color: "var(--brand-accent,#C75A1B)" }} />}
                    <span className="text-[13px] font-semibold text-[#1F1F1F]">{fileName || "Choose a CSV or XLSX file"}</span>
                    <span className="text-[11.5px]" style={{ color: "#64718a" }}>{parsed ? `${parsed.row_count} rows · ${parsed.columns.length} columns` : "One row per contact; rows roll up by project reference"}</span>
                </button>
            </div>

            {/* Mapping */}
            {parsed && (
                <div className="bg-white border rounded-[13px] shadow-brand-sm p-6" style={{ borderColor: "#E5E0DA" }} data-testid="import-mapping">
                    <h2 className="text-[14px] font-bold text-[#1F1F1F] flex items-center gap-2 mb-3"><Wand2 className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Map columns</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {fields.map((f) => (
                            <label key={f} className="block">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>{FIELD_LABELS[f] || f}</span>
                                <select data-testid={`import-map-${f}`} value={mapping[f] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value || undefined }))}
                                    className="w-full h-9 px-2.5 rounded-lg border bg-white text-sm" style={{ borderColor: mapping[f] ? "#CBE3C6" : "#E5E0DA" }}>
                                    <option value="">— not mapped —</option>
                                    {parsed.columns.map((col) => <option key={col} value={col}>{col}</option>)}
                                </select>
                            </label>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <button data-testid="import-validate-button" onClick={() => run(true)} disabled={busy}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[9px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0] disabled:opacity-60" style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />} Validate (dry-run)
                        </button>
                        <button data-testid="import-commit-button" onClick={() => run(false)} disabled={busy || !result || !result.ok || committed}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[9px] text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: "#1f2a44" }}>
                            <PlayCircle className="w-4 h-4" /> Import {parsed.row_count} row(s)
                        </button>
                        {result && !result.ok && <span className="text-[11.5px] font-semibold" style={{ color: "#B33A2E" }}>Resolve errors before importing.</span>}
                    </div>
                </div>
            )}

            {/* Result */}
            {result && (
                <div className="bg-white border rounded-[13px] shadow-brand-sm p-6" style={{ borderColor: "#E5E0DA" }} data-testid="import-result">
                    <div className="flex items-center gap-2 mb-3">
                        {committed ? <CheckCircle2 className="w-5 h-5" style={{ color: "#3F7D3A" }} /> : <FileSpreadsheet className="w-5 h-5" style={{ color: "var(--brand-accent,#C75A1B)" }} />}
                        <h2 className="text-[14px] font-bold text-[#1F1F1F]">{committed ? "Import complete" : "Validation preview"}</h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {[["sites", "Sites"], ["buildings", "Buildings"], ["projects", "Projects"], ["contacts", "Contacts"]].map(([k, l]) => (
                            <div key={k} data-testid={`import-count-${k}`} className="rounded-[10px] border px-3.5 py-3" style={{ borderColor: "#EFEAE3", background: "#FCFBF9" }}>
                                <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#64718a" }}>{l}</div>
                                <div className="text-[15px] font-bold text-[#1F1F1F] mt-1">{committed ? "" : "would "}create {c[k].created} · update {c[k].updated}</div>
                            </div>
                        ))}
                    </div>

                    {result.errors.length > 0 && (
                        <div className="rounded-[10px] border p-3 mb-3" style={{ borderColor: "#F0C9B7", background: "#FBE8E5" }} data-testid="import-errors">
                            <div className="text-[12px] font-bold flex items-center gap-1.5" style={{ color: "#B33A2E" }}><AlertTriangle className="w-3.5 h-3.5" /> {result.errors.length} error(s) — these rows will be skipped</div>
                            <ul className="mt-1.5 space-y-0.5 text-[11.5px]" style={{ color: "#7a2b22" }}>
                                {result.errors.slice(0, 25).map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                            </ul>
                        </div>
                    )}
                    {result.exceptions.length > 0 && (
                        <div className="rounded-[10px] border p-3 mb-3" style={{ borderColor: "#F0D9B5", background: "#FCF6EA" }} data-testid="import-exceptions">
                            <div className="text-[12px] font-bold flex items-center gap-1.5" style={{ color: "#C68A1C" }}><AlertTriangle className="w-3.5 h-3.5" /> {result.exceptions.length} exception(s) — imported but missing details</div>
                            <ul className="mt-1.5 space-y-0.5 text-[11.5px]" style={{ color: "#8a6410" }}>
                                {result.exceptions.slice(0, 25).map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                            </ul>
                        </div>
                    )}
                    {result.errors.length === 0 && result.exceptions.length === 0 && (
                        <div className="text-[12.5px] flex items-center gap-1.5" style={{ color: "#3F7D3A" }}><CheckCircle2 className="w-4 h-4" /> No errors or exceptions.</div>
                    )}

                    {committed && (
                        <button onClick={() => navigate("/app/projects")} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[9px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            View projects
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
